import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/store";
import AuthAction from "../store/auth/action";
import type { UserVerifiedContactInfo } from "../models/auth/auth.model";

const SignUp = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isLoading, error, signUpSuccess } = useAppSelector((state) => state.auth);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [instagram, setInstagram] = useState("");
    const [facebook, setFacebook] = useState("");
    const [tiktok, setTiktok] = useState("");
    const [phone, setPhone] = useState("");
    const [contactError, setContactError] = useState("");

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setContactError("");
        if (!firstName || !lastName || !dateOfBirth || !email || !password) return;
        const hasAtLeastOneContact = !!(instagram.trim() || facebook.trim() || tiktok.trim() || phone.trim());
        if (!hasAtLeastOneContact) {
            setContactError("Ju lutemi shënoni së paku njërin nga linkat e rrjeteve sociale apo numrin e telefonit.");
            return;
        }
        const contacts = {
            instagram: instagram.trim(),
            facebook: facebook.trim(),
            tiktok: tiktok.trim(),
            phone: phone.trim(),
        };
        const verifiedContacts: UserVerifiedContactInfo = {
            instagram: contacts.instagram ? "pending" : "",
            facebook: contacts.facebook ? "pending" : "",
            tiktok: contacts.tiktok ? "pending" : "",
            phone: contacts.phone ? "pending" : "",
        };
        AuthAction.signUp(
            {
                first_name: firstName,
                last_name: lastName,
                date_of_birth: dateOfBirth,
                email: email.trim(),
                password,
                contacts,
                verifiedContacts,
            },
            dispatch
        );
    };

    if (signUpSuccess) {
        AuthAction.clearState(dispatch);
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#f8f2ed] font-manrope">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm text-center">
                    <h2 className="text-xl font-semibold text-[#704e37] mb-2">Regjistrimi u krye</h2>
                    <p className="text-gray-600 mb-4">Shikoni email-in tuaj për të verifikuar llogarinë.</p>
                    <button
                        onClick={() => navigate("/login")}
                        className="bg-[#704e37] text-white font-medium py-2 px-4 rounded"
                    >
                        Shko te Kyçuni
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-[#f8f2ed] font-manrope py-8">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">
                <h2 className="text-2xl font-semibold mb-6 text-center text-[#704e37]">Regjistrohuni</h2>
                <form onSubmit={onSubmit} className="space-y-3">
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        placeholder="Emri"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        placeholder="Mbiemri"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="date"
                        placeholder="Data e lindjes"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        required
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="password"
                        placeholder="Fjalëkalimi"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <p className="text-xs text-gray-500 mt-2">Së paku një kontakt (për të kontaktuar me blerësit)</p>
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="url"
                        placeholder="Instagram (linku i profilit)"
                        value={instagram}
                        onChange={(e) => setInstagram(e.target.value)}
                        autoComplete="off"
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="url"
                        placeholder="Facebook (linku i profilit)"
                        value={facebook}
                        onChange={(e) => setFacebook(e.target.value)}
                        autoComplete="off"
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="url"
                        placeholder="TikTok (linku i profilit)"
                        value={tiktok}
                        onChange={(e) => setTiktok(e.target.value)}
                        autoComplete="off"
                    />
                    <input
                        className="w-full border rounded py-2 px-3 focus:ring-1 focus:ring-[#ebcaaa]"
                        type="tel"
                        placeholder="Telefoni (WhatsApp)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoComplete="tel"
                    />
                    {(contactError || error) && <p className="text-xs text-red-500">{contactError || error}</p>}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#704e37] text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                    >
                        {isLoading ? "Prisni..." : "Regjistrohuni"}
                    </button>
                </form>
                <p className="text-center text-sm text-gray-600 mt-4">
                    Keni llogari? <Link to="/login" className="text-[#704e37] font-medium">Kyçuni</Link>
                </p>
            </div>
        </div>
    );
};

export default SignUp;
